'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function MathCalculator() {
  const t = useTranslations("tools.mathCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [currentInput, setCurrentInput] = useState('');
  const [previousInput, setPreviousInput] = useState('');
  const [operation, setOperation] = useState<string | null>(null);
  const [shouldResetScreen, setShouldResetScreen] = useState(false);
  const [history, setHistory] = useState<string[]>([]);

  // Quick calculators state
  const [percentValue, setPercentValue] = useState('');
  const [percentOf, setPercentOf] = useState('');
  const [sideA, setSideA] = useState('');
  const [sideB, setSideB] = useState('');
  const [avgNumbers, setAvgNumbers] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9' || e.key === '.') {
        appendNumber(e.key);
      } else if (e.key === 'Enter') {
        calculate();
      } else if (e.key === 'Backspace') {
        handleBack();
      } else if (e.key === 'Escape') {
        handleClear();
      } else if (['+', '-', '*', '/'].includes(e.key)) {
        handleOperator(e.key);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentInput, previousInput, operation]);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const appendNumber = (number: string) => {
    if (shouldResetScreen) {
      setCurrentInput('');
      setShouldResetScreen(false);
    }

    if (number === '.' && currentInput.includes('.')) return;
    if (currentInput === '0' && number !== '.') {
      setCurrentInput(number);
    } else {
      setCurrentInput(currentInput + number);
    }
  };

  const handleClear = () => {
    setCurrentInput('');
    setPreviousInput('');
    setOperation(null);
  };

  const handleBack = () => {
    setCurrentInput(currentInput.slice(0, -1));
  };

  const handleOperator = (op: string) => {
    if (currentInput === '') return;
    if (previousInput !== '') {
      calculate();
    }
    setOperation(op);
    setPreviousInput(currentInput);
    setShouldResetScreen(true);
  };

  const calculate = () => {
    if (operation === null || previousInput === '') return;

    const prev = parseFloat(previousInput);
    const current = parseFloat(currentInput);
    let result: number | string;

    switch(operation) {
      case '+':
        result = prev + current;
        break;
      case '-':
        result = prev - current;
        break;
      case '*':
        result = prev * current;
        break;
      case '/':
        result = current !== 0 ? prev / current : 'Error';
        break;
      case '^':
        result = Math.pow(prev, current);
        break;
      default:
        return;
    }

    const operatorSymbol = operation === '*' ? '×' : operation === '/' ? '÷' : operation;
    addToHistory(`${previousInput} ${operatorSymbol} ${currentInput} = ${result}`);

    setCurrentInput(result.toString());
    setOperation(null);
    setPreviousInput('');
    setShouldResetScreen(true);
  };

  const handleFunction = (func: string) => {
    if (!currentInput) return;

    const value = parseFloat(currentInput);
    let result: number;
    let historyEntry: string;

    switch(func) {
      case 'sqrt':
        result = Math.sqrt(value);
        historyEntry = `√${currentInput} = ${result}`;
        break;
      case 'square':
        result = Math.pow(value, 2);
        historyEntry = `${currentInput}² = ${result}`;
        break;
      case 'percent':
        result = value / 100;
        setCurrentInput(result.toString());
        return;
      case 'negate':
        result = value * -1;
        setCurrentInput(result.toString());
        return;
      case 'sin':
        result = Math.sin(value * Math.PI / 180);
        historyEntry = `sin(${currentInput}°) = ${result.toFixed(8)}`;
        break;
      case 'cos':
        result = Math.cos(value * Math.PI / 180);
        historyEntry = `cos(${currentInput}°) = ${result.toFixed(8)}`;
        break;
      case 'tan':
        result = Math.tan(value * Math.PI / 180);
        historyEntry = `tan(${currentInput}°) = ${result.toFixed(8)}`;
        break;
      case 'log':
        result = Math.log10(value);
        historyEntry = `log(${currentInput}) = ${result}`;
        break;
      case 'ln':
        result = Math.log(value);
        historyEntry = `ln(${currentInput}) = ${result}`;
        break;
      case 'pi':
        setCurrentInput(Math.PI.toString());
        setShouldResetScreen(true);
        return;
      case 'e':
        setCurrentInput(Math.E.toString());
        setShouldResetScreen(true);
        return;
      case 'power':
        setPreviousInput(currentInput);
        setOperation('^');
        setShouldResetScreen(true);
        return;
      default:
        return;
    }

    addToHistory(historyEntry);
    setCurrentInput(result.toString());
    setShouldResetScreen(true);
  };

  const addToHistory = (entry: string) => {
    setHistory(prev => [entry, ...prev].slice(0, 10));
  };

  const clearHistory = () => {
    setHistory([]);
  };

  const calculatePercent = () => {
    const val = parseFloat(percentValue);
    const of = parseFloat(percentOf);
    if (!isNaN(val) && !isNaN(of)) {
      const result = (val / 100) * of;
      return `${val}% ${t('percentOf')} ${of} = ${result.toFixed(2)}`;
    }
    return t('enterValues');
  };

  const calculatePythag = () => {
    const a = parseFloat(sideA);
    const b = parseFloat(sideB);
    if (!isNaN(a) && !isNaN(b)) {
      const c = Math.sqrt(a * a + b * b);
      return `c = ${c.toFixed(4)}`;
    }
    return t('enterValues');
  };

  const calculateAverage = () => {
    const input = avgNumbers.trim();
    if (!input) return t('enterValues');

    const numbers = input.split(',').map(n => parseFloat(n.trim())).filter(n => !isNaN(n));
    if (numbers.length > 0) {
      const sum = numbers.reduce((a, b) => a + b, 0);
      const avg = sum / numbers.length;
      return `${t('average')} = ${avg.toFixed(2)}`;
    }
    return t('enterValidNumbers');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      {
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative' as const,
        zIndex: 1,
      },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      {
        textAlign: 'center' as const,
        marginBottom: '2rem',
      },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      {
        fontWeight: 700,
        color: colors.textPrimary,
        marginBottom: '1rem',
        lineHeight: 1.2,
      },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    subtitle: getResponsiveStyle(
      {
        color: colors.textSecondary,
        marginBottom: '0',
      },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorSection: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem' },
      { padding: '3rem 1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      {
        maxWidth: '900px',
        margin: '0 auto',
      },
      {},
      {},
      {}
    ),
    toolHeader: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    h2: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
        marginBottom: '0.5rem',
      },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    toolSubtitle: getResponsiveStyle(
      {
        color: colors.textSecondary,
        margin: '0',
      },
      { fontSize: '0.875rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    calculatorCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    calcDisplay: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        borderRadius: '0.75rem',
        marginBottom: '1rem',
        minHeight: '100px',
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    calcInput: getResponsiveStyle(
      {
        color: '#94a3b8',
        marginBottom: '0.5rem',
        minHeight: '1.5rem',
        wordBreak: 'break-all' as const,
      },
      { fontSize: '0.875rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    calcOutput: getResponsiveStyle(
      {
        color: 'white',
        fontWeight: 600,
        wordBreak: 'break-all' as const,
      },
      { fontSize: '2rem' },
      { fontSize: '2.25rem' },
      { fontSize: '2.5rem' }
    ),
    calcButtons: getResponsiveStyle(
      {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '0.75rem',
      },
      { gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' },
      { gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.625rem' },
      { gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem' }
    ),
    calcBtn: getResponsiveStyle(
      {
        background: '#f1f5f9',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s',
        color: '#334155',
      },
      { padding: '1rem 0.5rem', fontSize: '1rem' },
      { padding: '1.125rem 0.5rem', fontSize: '1.0625rem' },
      { padding: '1.25rem 0.5rem', fontSize: '1.125rem' }
    ),
    operatorBtn: {
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white',
    },
    equalsBtn: {
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: 'white',
    },
    clearBtn: {
      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      color: 'white',
    },
    functionBtn: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
        color: 'white',
      },
      { fontSize: '0.875rem' },
      { fontSize: '0.9375rem' },
      { fontSize: '0.95rem' }
    ),
    historySection: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem',
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    historyHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
    },
    h3: getResponsiveStyle(
      {
        color: colors.textPrimary,
        margin: '0',
      },
      { fontSize: '1.125rem' },
      { fontSize: '1.1875rem' },
      { fontSize: '1.25rem' }
    ),
    clearHistoryBtn: getResponsiveStyle(
      {
        background: '#ef4444',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        transition: 'all 0.2s',
      },
      { padding: '0.5rem 0.75rem', fontSize: '0.8125rem' },
      { padding: '0.5rem 0.875rem', fontSize: '0.875rem' },
      { padding: '0.5rem 1rem', fontSize: '0.875rem' }
    ),
    historyList: {
      maxHeight: '200px',
      overflowY: 'auto' as const,
    },
    historyItem: getResponsiveStyle(
      {
        padding: '0.75rem',
        borderBottom: '1px solid #e2e8f0',
        color: '#475569',
        fontFamily: "'Courier New', monospace",
      },
      { fontSize: '0.875rem' },
      { fontSize: '0.9375rem' },
      { fontSize: '1rem' }
    ),
    historyEmpty: {
      textAlign: 'center' as const,
      color: '#94a3b8',
      padding: '2rem',
      fontStyle: 'italic',
    },
    quickCalcs: getResponsiveStyle(
      {
        display: 'grid',
        gap: '1rem',
        marginTop: '2rem',
      },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(3, 1fr)' }
    ),
    quickCalcCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        borderLeft: '4px solid #2563eb',
      },
      { padding: '1.25rem' },
      { padding: '1.375rem' },
      { padding: '1.5rem' }
    ),
    h4: getResponsiveStyle(
      {
        color: colors.textPrimary,
        margin: '0 0 1rem 0',
      },
      { fontSize: '0.9375rem' },
      { fontSize: '0.96875rem' },
      { fontSize: '1rem' }
    ),
    quickCalcInput: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '0.75rem',
      flexDirection: screenSize === 'mobile' ? 'column' as const : 'row' as const,
    },
    input: getResponsiveStyle(
      {
        flex: 1,
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
      },
      { padding: '0.5rem', fontSize: '0.875rem' },
      { padding: '0.5625rem', fontSize: '0.875rem' },
      { padding: '0.5rem', fontSize: '0.875rem' }
    ),
    quickCalcResult: getResponsiveStyle(
      {
        background: '#f0f9ff',
        borderRadius: '0.5rem',
        color: colors.textPrimary,
        fontWeight: 600,
        textAlign: 'center' as const,
        minHeight: '2.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      { padding: '0.625rem', fontSize: '0.875rem' },
      { padding: '0.6875rem', fontSize: '0.9375rem' },
      { padding: '0.75rem', fontSize: '1rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      {
        lineHeight: 1.7,
        color: colors.textSecondary,
        marginBottom: '1rem',
      },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      {
        lineHeight: 1.8,
        color: colors.textSecondary,
        paddingLeft: '1.5rem',
      },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
        marginBottom: '0.5rem',
      },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.1875rem' }
    ),
    faqAnswer: getResponsiveStyle(
      {
        color: colors.textSecondary,
        lineHeight: 1.7,
      },
      { fontSize: '0.9375rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' }
    ),
    reference: {
      marginBottom: '0.75rem',
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      transition: 'color 0.2s',
    },
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.subtitle}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorSection}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>🧮 {t('toolTitle')}</h2>
            <p style={styles.toolSubtitle}>{t('toolSubtitle')}</p>
          </div>

          <div style={styles.calculatorCard}>
            <div style={styles.calcDisplay}>
              <div style={styles.calcInput}>
                {previousInput && operation && `${previousInput} ${operation === '*' ? '×' : operation === '/' ? '÷' : operation}`}
              </div>
              <div style={styles.calcOutput}>{currentInput || '0'}</div>
            </div>

            <div style={styles.calcButtons}>
              <button
                style={{...styles.calcBtn, ...styles.functionBtn}}
                onClick={() => handleFunction('sqrt')}
              >
                √
              </button>
              <button
                style={{...styles.calcBtn, ...styles.functionBtn}}
                onClick={() => handleFunction('square')}
              >
                x²
              </button>
              <button
                style={{...styles.calcBtn, ...styles.functionBtn}}
                onClick={() => handleFunction('percent')}
              >
                %
              </button>
              <button
                style={{...styles.calcBtn, ...styles.clearBtn}}
                onClick={handleClear}
              >
                AC
              </button>
              <button
                style={{...styles.calcBtn, ...styles.operatorBtn}}
                onClick={handleBack}
              >
                ⌫
              </button>

              <button
                style={{...styles.calcBtn, ...styles.functionBtn}}
                onClick={() => handleFunction('sin')}
              >
                sin
              </button>
              <button
                style={{...styles.calcBtn, ...styles.functionBtn}}
                onClick={() => handleFunction('cos')}
              >
                cos
              </button>
              <button
                style={{...styles.calcBtn, ...styles.functionBtn}}
                onClick={() => handleFunction('tan')}
              >
                tan
              </button>
              <button
                style={{...styles.calcBtn, ...styles.functionBtn}}
                onClick={() => handleFunction('log')}
              >
                log
              </button>
              <button
                style={{...styles.calcBtn, ...styles.functionBtn}}
                onClick={() => handleFunction('ln')}
              >
                ln
              </button>

              <button style={styles.calcBtn} onClick={() => appendNumber('7')}>7</button>
              <button style={styles.calcBtn} onClick={() => appendNumber('8')}>8</button>
              <button style={styles.calcBtn} onClick={() => appendNumber('9')}>9</button>
              <button
                style={{...styles.calcBtn, ...styles.operatorBtn}}
                onClick={() => handleOperator('/')}
              >
                ÷
              </button>
              <button
                style={{...styles.calcBtn, ...styles.functionBtn}}
                onClick={() => handleFunction('power')}
              >
                xʸ
              </button>

              <button style={styles.calcBtn} onClick={() => appendNumber('4')}>4</button>
              <button style={styles.calcBtn} onClick={() => appendNumber('5')}>5</button>
              <button style={styles.calcBtn} onClick={() => appendNumber('6')}>6</button>
              <button
                style={{...styles.calcBtn, ...styles.operatorBtn}}
                onClick={() => handleOperator('*')}
              >
                ×
              </button>
              <button
                style={{...styles.calcBtn, ...styles.functionBtn}}
                onClick={() => handleFunction('pi')}
              >
                π
              </button>

              <button style={styles.calcBtn} onClick={() => appendNumber('1')}>1</button>
              <button style={styles.calcBtn} onClick={() => appendNumber('2')}>2</button>
              <button style={styles.calcBtn} onClick={() => appendNumber('3')}>3</button>
              <button
                style={{...styles.calcBtn, ...styles.operatorBtn}}
                onClick={() => handleOperator('-')}
              >
                −
              </button>
              <button
                style={{...styles.calcBtn, ...styles.functionBtn}}
                onClick={() => handleFunction('e')}
              >
                e
              </button>

              <button style={styles.calcBtn} onClick={() => appendNumber('0')}>0</button>
              <button style={styles.calcBtn} onClick={() => appendNumber('.')}>.</button>
              <button
                style={{...styles.calcBtn, ...styles.functionBtn}}
                onClick={() => handleFunction('negate')}
              >
                ±
              </button>
              <button
                style={{...styles.calcBtn, ...styles.operatorBtn}}
                onClick={() => handleOperator('+')}
              >
                +
              </button>
              <button
                style={{...styles.calcBtn, ...styles.equalsBtn}}
                onClick={calculate}
              >
                =
              </button>
            </div>
          </div>

          <div style={styles.historySection}>
            <div style={styles.historyHeader}>
              <h3 style={styles.h3}>📜 {t('historyTitle')}</h3>
              <button style={styles.clearHistoryBtn} onClick={clearHistory}>
                {t('clearHistory')}
              </button>
            </div>
            <div style={styles.historyList}>
              {history.length === 0 ? (
                <div style={styles.historyEmpty}>{t('noCalculations')}</div>
              ) : (
                history.map((item, index) => (
                  <div key={index} style={styles.historyItem}>{item}</div>
                ))
              )}
            </div>
          </div>

          <div style={styles.quickCalcs}>
            <div style={styles.quickCalcCard}>
              <h4 style={styles.h4}>🔢 {t('percentageCalculator')}</h4>
              <div style={styles.quickCalcInput}>
                <input
                  type="number"
                  style={styles.input}
                  placeholder={t('value')}
                  value={percentValue}
                  onChange={(e) => setPercentValue(e.target.value)}
                  step="any"
                />
                <input
                  type="number"
                  style={styles.input}
                  placeholder={t('of')}
                  value={percentOf}
                  onChange={(e) => setPercentOf(e.target.value)}
                  step="any"
                />
              </div>
              <div style={styles.quickCalcResult}>{calculatePercent()}</div>
            </div>

            <div style={styles.quickCalcCard}>
              <h4 style={styles.h4}>📐 {t('pythagoreanTheorem')}</h4>
              <div style={styles.quickCalcInput}>
                <input
                  type="number"
                  style={styles.input}
                  placeholder={t('sideA')}
                  value={sideA}
                  onChange={(e) => setSideA(e.target.value)}
                  step="any"
                />
                <input
                  type="number"
                  style={styles.input}
                  placeholder={t('sideB')}
                  value={sideB}
                  onChange={(e) => setSideB(e.target.value)}
                  step="any"
                />
              </div>
              <div style={styles.quickCalcResult}>{calculatePythag()}</div>
            </div>

            <div style={styles.quickCalcCard}>
              <h4 style={styles.h4}>📊 {t('averageCalculator')}</h4>
              <div style={styles.quickCalcInput}>
                <input
                  type="text"
                  style={styles.input}
                  placeholder={t('enterNumbers')}
                  value={avgNumbers}
                  onChange={(e) => setAvgNumbers(e.target.value)}
                />
              </div>
              <div style={styles.quickCalcResult}>{calculateAverage()}</div>
            </div>
          </div>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToUseStep1')}</li>
          <li style={styles.listItem}>{t('howToUseStep2')}</li>
          <li style={styles.listItem}>{t('howToUseStep3')}</li>
          <li style={styles.listItem}>{t('howToUseStep4')}</li>
          <li style={styles.listItem}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <p style={styles.paragraph}>{t('insightsParagraph1')}</p>
        <p style={styles.paragraph}>{t('insightsParagraph2')}</p>
        <p style={styles.paragraph}>{t('insightsParagraph3')}</p>
        <p style={styles.paragraph}>{t('insightsParagraph4')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <h3 style={styles.h3}>{t('detailsSubtitle1')}</h3>
        <p style={styles.paragraph}>{t('detailsParagraph1')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsFeature1')}</li>
          <li style={styles.listItem}>{t('detailsFeature2')}</li>
          <li style={styles.listItem}>{t('detailsFeature3')}</li>
          <li style={styles.listItem}>{t('detailsFeature4')}</li>
          <li style={styles.listItem}>{t('detailsFeature5')}</li>
        </ul>

        <h3 style={styles.h3}>{t('detailsSubtitle2')}</h3>
        <p style={styles.paragraph}>{t('detailsParagraph2')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsBenefit1')}</li>
          <li style={styles.listItem}>{t('detailsBenefit2')}</li>
          <li style={styles.listItem}>{t('detailsBenefit3')}</li>
          <li style={styles.listItem}>{t('detailsBenefit4')}</li>
          <li style={styles.listItem}>{t('detailsBenefit5')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion1')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer1')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion2')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer2')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion3')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer3')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion4')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer4')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion5')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer5')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.reference}>
            <a
              href="https://tutorbin.com/blog/online-math-calculator-7-ways-it-will-help-students"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference1')}
            </a>
          </li>
          <li style={styles.reference}>
            <a
              href="https://healthandcare.fitness.blog/2025/01/29/the-ultimate-guide-to-online-scientific-calculators-features-benefits-and-best-picks/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference2')}
            </a>
          </li>
          <li style={styles.reference}>
            <a
              href="https://www.edisonos.com/online-teaching/graphing-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference3')}
            </a>
          </li>
          <li style={styles.reference}>
            <a
              href="https://theeduassist.com/mobile-apps-for-math-problem-solving-practice-in-2025/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference4')}
            </a>
          </li>
          <li style={styles.reference}>
            <a
              href="https://www.piqosity.com/digital-sat-calculator-act-calculator-tips/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
            >
              {t('reference5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}